import type {
  PipelineHistoryField,
  PipelineHistoryFieldChange,
} from '@repo/lakehouse-sdk';

const COLUMN_PATHS = [
  '/native_stream_properties/schema/columns',
  '/nativeStreamProperties/schema/columns',
];

const columnNames = (
  fields: PipelineHistoryField[],
  path: string
): Map<string, string> | undefined => {
  const root = fields.find((field) => field.path === path);
  if (root && root.kind !== 'ARRAY') {
    return undefined;
  }
  const items = new Map<string, string>();
  const names = new Set<string>();
  const byPath = new Map(fields.map((field) => [field.path, field]));
  for (const field of fields) {
    if (!field.path.startsWith(`${path}/`)) {
      continue;
    }
    const index = field.path.slice(path.length + 1).split('/')[0];
    const itemPath = `${path}/${index}`;
    if (items.has(itemPath)) {
      continue;
    }
    const name = byPath.get(`${itemPath}/name`);
    if (
      !/^(0|[1-9]\d*)$/.test(index) ||
      byPath.get(itemPath)?.kind !== 'OBJECT' ||
      name?.kind !== 'STRING' ||
      !name.value ||
      names.has(name.value)
    ) {
      return undefined;
    }
    names.add(name.value);
    items.set(itemPath, name.value);
  }
  return items;
};

// Only schema columns have order-independent identity. Ambiguous names fall back
// to the original positional diff so no accepted values are silently merged.
export const normalizeHistoryColumns = (
  snapshots: PipelineHistoryField[][]
): { snapshots: PipelineHistoryField[][]; names: Map<string, string> } => {
  const mappings = snapshots.map(() => new Map<string, string>());
  const names = new Map<string, string>();
  for (const path of COLUMN_PATHS) {
    const candidates = snapshots.map((fields) => columnNames(fields, path));
    if (candidates.some((candidate) => !candidate)) {
      continue;
    }
    candidates.forEach((candidate, index) => {
      candidate?.forEach((name, itemPath) => {
        const namedPath = `${path}/${name.replaceAll('~', '~0').replaceAll('/', '~1')}`;
        mappings[index].set(itemPath, namedPath);
        names.set(namedPath, name);
      });
    });
  }
  return {
    names,
    snapshots: snapshots.map((fields, index) =>
      fields.flatMap((field) => {
        const segments = field.path.split('/');
        const itemPath = segments.slice(0, 5).join('/');
        const namedPath = mappings[index].get(itemPath);
        if (!namedPath) {
          return [field];
        }
        if (field.path === `${itemPath}/name`) {
          return [];
        }
        return [
          { ...field, path: namedPath + field.path.slice(itemPath.length) },
        ];
      })
    ),
  };
};

export const namedColumnChanges = (
  changes: PipelineHistoryFieldChange[],
  snapshot: PipelineHistoryField[]
): {
  changes: PipelineHistoryFieldChange[];
  snapshot: PipelineHistoryField[];
  names: Map<string, string>;
} => {
  const after = new Map(snapshot.map((field) => [field.path, field]));
  for (const change of changes) {
    if (change.after) {
      after.set(change.path, change.after);
    } else {
      after.delete(change.path);
    }
  }
  const before = new Map(after);
  for (const change of changes) {
    if (change.before) {
      before.set(change.path, change.before);
    } else {
      before.delete(change.path);
    }
  }
  const normalized = normalizeHistoryColumns([
    Array.from(before.values()),
    Array.from(after.values()),
  ]);
  const [oldFields, newFields] = normalized.snapshots;
  const oldByPath = new Map(oldFields.map((field) => [field.path, field]));
  const newByPath = new Map(newFields.map((field) => [field.path, field]));
  const paths = new Set([...oldByPath.keys(), ...newByPath.keys()]);
  return {
    names: normalized.names,
    snapshot: newFields,
    changes: Array.from(paths).flatMap((path) => {
      const before = oldByPath.get(path);
      const after = newByPath.get(path);
      return before?.kind === after?.kind && before?.value === after?.value
        ? []
        : [{ path, before, after }];
    }),
  };
};
